import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FILM_PENDING, FILM_PLAYING, LOADER_DONE } from "./Loader";
import { initHeroWordmark, initReveals, fitTextToWidth } from "../motion/reveals";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import { BRANDS } from "./Nav";
import { useAutoplayFilm } from "../hooks/useAutoplayFilm";
import { PHONE_HD } from "../lib/hdSlides";

// Rotating hero images — daytime motorsport photography only.
// Subject weighted right-of-frame to leave left space for headline overlay.
// HD slides (client, 2026-10-10: "the originals are much higher quality").
// Each landing frame is now cut from the full-resolution original at 1920 /
// 2560 / 3840 wide (assets_raw/cut_heroes_hd.py - same crop boxes as before),
// output-sharpened, encoded AVIF q72 + WebP q90, and served as a <picture>
// with srcset + sizes=100vw so a 1x laptop downloads the 1920 file while a
// 2x MacBook / 5K iMac gets the 3840 one at native pixels instead of a 2400px
// q82 WebP upscaled 1.2-2x. `hd` = /photos/hero/<name>, `widths` = the
// rendered sizes (the last one is the crop's native width when smaller than 3840).
const HD = (name, widths, pos) => ({
  src: `/photos/hero/${name}-1920.webp`, // fallback (no srcset support)
  sources: [{ type: "image/avif", srcSet: widths.map((w) => `/photos/hero/${name}-${w}.avif ${w}w`).join(", ") }],
  srcSet: widths.map((w) => `/photos/hero/${name}-${w}.webp ${w}w`).join(", "),
  hd: true,
  pos,
});
// Client-delivered frames (2026-10-10) that only exist at web resolution
// (1920-2000px): shipped at native size, never upscaled, and NOT re-compressed
// unless there is a real saving. `src` is the fallback, `sources` optional.
const NATIVE = (src, pos, sources = []) => ({ src, sources, srcSet: undefined, hd: true, pos });
const DEFAULT_SLIDES = [
  // Client photography. Approved order (client, 2026-10-10).
  // Slot 1: Yas Marina pit lane, yellow Supra #23 with the two hosts - the
  // client's own caption-free file (gmsite-4, 2000x1333), shipped UNTOUCHED
  // (client, 2026-10-10: "just use the one the client sent").
  NATIVE("/photos/hero/hero-gm-14-supra-23-hosts-client.webp", "50% 50%"),
  // Porsche grid under the Dubai Autodrome 20-years gantry (gmsite-2).
  // Delivered as a 2000x1125 lossy WebP - a second encode would only add
  // loss, so the file ships untouched (643KB).
  NATIVE("/photos/hero/hero-gm-9-autodrome-grid-2000.webp", "50% 50%"),
  // Blue BRZ #06 on the Autodrome banking (gmsite-1, 1920x1080 JPG 1.2MB).
  // Main file = AVIF q85 4:4:4 (301KB, PSNR 42dB vs the JPG = visually
  // lossless); the untouched JPG is the fallback.
  NATIVE("/photos/hero/hero-gm-10-brz-06-autodrome-1920.jpg", "55% 50%", [{ type: "image/avif", srcSet: "/photos/hero/hero-gm-10-brz-06-autodrome-1920.avif" }]),
  // Yas Marina group under the gantry - the client's own graded 16:9 edit
  // (gmsite-3, 2000x1125), shipped UNTOUCHED (client, 2026-10-10).
  NATIVE("/photos/hero/hero-gm-12-yas-group-client.webp", "50% 50%"),
  // Golden paddock moved from slot 3 to slot 5 (client, 2026-10-10).
  HD("hero-gm-2-golden-paddock", [1920, 2560, 3840], "60% 50%"), // community frame: sunset, cars and people (client, 2026-09-16; restored 2026-09-17)
  // hero-gm-4-sunset-paddock removed from the rotation (client, 2026-10-10).
];
// Phones (client, 2026-09-17): portrait originals shown whole-frame in
// /photos/mobile (assets_raw/cut_mobile.py). The McLaren under the Yas gantry
// leads (car first); then the group, the GT3 RS wing, the pit lane, sunset.
// HD (client, 2026-10-11): same frames re-cut from the originals at 1080 and
// 1620 wide, AVIF + WebP, served via <picture> (assets_raw/cut_ms_hd.py).
const PHONE_SLIDES = [
  PHONE_HD("hero-yas-mclaren", [1080, 1620], "50% 50%"),
  PHONE_HD("hero-garage-yellow", [1080, 1620], "50% 50%"), // through the garage, two yellow Porsches, a word with the driver leaving (client pick, 2026-09-17)
  PHONE_HD("hero-gt3rs-wing", [1080, 1620], "50% 50%"), // GT3 RS wing stays on phones (client, 2026-09-18: the grey Supra is desktop-only)
  PHONE_HD("hero-pitlane-above", [1080, 1620], "50% 50%"),
  PHONE_HD("hero-sunset-paddock", [1080, 1620], "50% 50%"),
];
const DEFAULT_CTAS = [
  // Single CTA (client, 2026-09-09): "Enter a season" pointed at /contact and was removed.
  { label: "Explore Ultimate Lap", href: "/events/ultimate-lap", white: true, wide: true, testid: "hero-cta-primary" }, // white, yellow on hover (client, 2026-09-17); see-through on phones
];
// V2 is photos only again (client, 2026-09-19: "undo all the video work" in
// the rotation). Hold restored to 4800 (client, 2026-09-17). The living
// slides are gone; `film` (V1) is the only video path left in this component.
const SLIDE_MS = 4800;

/*
  ONE hero for every wordmark page. The landing page and the Ultimate Lap
  microsite render this same component with a different `brand`, `slides`,
  `title` and `ctas`; the wordmark -> nav-logo morph mechanics below are
  shared and BAKED IN (see /app/memory/HERO_WORDMARK_AUDIT.md). Do not fork.
*/
/* The <video> inside a living slide. Plays from 0 while its slide is active,
   pauses otherwise, so every entrance starts on the poster frame. No loop:
   the clip is as long as the slide hold, and its last frame (already
   dissolved towards the first) simply holds through the crossfade out.
   Muted attribute set on the element too - iOS autoplay wants it. */
function HeroVideo({ src, poster, pos, active, paused, testid, loop = false, onPlaying }) {
  const ref = useRef(null);
  // Rewind ONLY when the slide becomes active - not when `paused` (hero
  // scrolled out / back in) toggles mid-turn, or the clip would restart.
  const wasActive = useRef(false);
  const live = !!(active && !paused);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    try {
      if (live && !wasActive.current) {
        // Fresh turn: start from the poster frame. Safe before metadata too.
        try { if (el.readyState >= 1) el.currentTime = 0; } catch { /* ignore */ }
      }
    } catch {
      /* media element quirks must never surface as runtime errors */
    }
    wasActive.current = active;
    return undefined;
  }, [active, live]);
  // Safari (2026-10-17): the play()/pause() itself, with retries on visibility,
  // readiness, first gesture and a stall watchdog - hooks/useAutoplayFilm.js.
  useAutoplayFilm(ref, live);
  return (
    <video
      ref={ref}
      className="gm-hero__video"
      src={src}
      poster={poster}
      muted
      loop={loop}
      playsInline
      preload="auto"
      style={{ objectPosition: pos }}
      data-testid={testid}
      onPlaying={onPlaying}
      onTimeUpdate={(e) => { if (e.currentTarget.currentTime > 0.05) onPlaying?.(); }}
    />
  );
}

export default function Hero({
  navLogoRef,
  navWordmarkRef,
  navMorphRef,
  brand = BRANDS.gridmasters,
  slides,
  title = "Automotive events, executed with precision", // client line, 2026-10-07 - no full stop
  ctas = DEFAULT_CTAS,
  testid = "hero-section",
  // Client, 2026-09-17: on phones the hero carries NO wordmark and no morph -
  // the nav shows its static lockup top-left. `phone` also picks the portrait
  // frame set when no explicit slides are passed.
  morph = true,
  phone = false,
  film = undefined,
  // Ultimate Lap keeps its yellow primary + ghost rulebook pair on phones
  // (client, 2026-09-17: "let those be"); the landing drops the fill.
  keepFill = false,
}) {
  // V1 (client, 2026-09-19): `film` = { src, poster } - the hero is that one
  // looping film, no slides, no progress bar, no Ken Burns. V2 = the slides.
  const SLIDES = film
    ? [{ video: film.src, poster: film.poster, pos: film.pos || "50% 50%", loop: true }]
    : slides || (phone ? PHONE_SLIDES : DEFAULT_SLIDES);
  // Film hero: tell the page Loader to hold the site behind the shine mark
  // until the film is really playing (client, 2026-09-19).
  const filmAnnounced = useRef(false);
  useEffect(() => {
    if (!film) return undefined;
    filmAnnounced.current = false;
    window.dispatchEvent(new CustomEvent(FILM_PENDING));
    return undefined;
  }, [film]);
  const onFilmPlaying = () => {
    if (filmAnnounced.current) return;
    filmAnnounced.current = true;
    window.dispatchEvent(new CustomEvent(FILM_PLAYING));
  };
  const heroRef = useRef(null);
  const wordmarkRef = useRef(null);
  const wordmarkTrackRef = useRef(null);
  const contentRef = useRef(null);
  const slideRefs = useRef([]);
  const [active, setActive] = useState(0);
  const [tick, setTick] = useState(0);
  const [reduce, setReduce] = useState(false);
  const [paused, setPaused] = useState(false);
  // Hold the rotation (and the Ken Burns drift) on slide 0 while the page
  // Loader is still up. The slideshow used to run behind the shield, so on a
  // slow line the site was revealed onto whichever slide the clock had reached
  // - often one that had not downloaded yet: a black hero (slow-line trace,
  // 2026-10-22). Slide 0 is the preloaded LCP frame; it is always the first
  // frame the visitor sees, and its drift starts the moment the site appears.
  const [gated, setGated] = useState(() => typeof window !== "undefined" && !window.__gmLoaderDone);
  useEffect(() => {
    if (!gated) return undefined;
    if (window.__gmLoaderDone) { setGated(false); return undefined; }
    const open = () => setGated(false);
    window.addEventListener(LOADER_DONE, open, { once: true });
    return () => window.removeEventListener(LOADER_DONE, open);
  }, [gated]);
  // Before measurement the nav lockup is hidden and the hero placeholder is
  // shown, so the very first paint already has the giant wordmark in place.
  useLayoutEffect(() => {
    document.body.setAttribute("data-wm-state", morph ? "boot" : "static");
    return () => document.body.removeAttribute("data-wm-state");
  }, [morph]);

  useEffect(() => {
    setReduce(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    setPaused(document.hidden);
    const onVis = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Pause the rotation when hero is offscreen — no wasted paints/scaling
  // when the user has scrolled past.
  useEffect(() => {
    const el = heroRef.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      (entries) => setPaused((p) => (document.hidden ? true : !entries[0].isIntersecting)),
      { threshold: 0.05 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Deterministic auto-advance: one effect, one timer. The next slide is
  // only brought in once its photo has actually decoded (slow-line trace,
  // 2026-10-22: with nothing lazy, every photo on the page downloads at once
  // and a hero frame can lose the race - crossfading to a frame that is not
  // there yet painted a black hero). Hold the current frame until it is.
  const slideCount = SLIDES.length;
  useEffect(() => {
    if (reduce || paused || gated) return undefined;
    let cancelled = false;
    let img = null;
    const advance = () => { if (!cancelled) setActive((cur) => (cur + 1) % slideCount); };
    const onReady = () => { img?.removeEventListener("load", onReady); img?.removeEventListener("error", onReady); advance(); };
    const id = setTimeout(() => {
      const next = (active + 1) % slideCount;
      img = slideRefs.current[next]?.querySelector("img") || null;
      if (img && !img.complete) {
        img.addEventListener("load", onReady, { once: true });
        img.addEventListener("error", onReady, { once: true });
        return;
      }
      advance();
    }, SLIDE_MS);
    return () => { cancelled = true; clearTimeout(id); img?.removeEventListener("load", onReady); img?.removeEventListener("error", onReady); };
  }, [active, tick, reduce, paused, gated, slideCount]);

  // Only the active and the upcoming slide carry a background image; the rest
  // are fetched when they come up. First slide is preloaded from index.html.
  const [seen, setSeen] = useState(() => new Set([0, 1 % Math.max(1, SLIDES.length)]));
  useEffect(() => {
    setSeen((prev) => {
      const nxt = new Set(prev);
      nxt.add(active);
      nxt.add((active + 1) % SLIDES.length);
      return nxt.size === prev.size ? prev : nxt;
    });
  }, [active, SLIDES.length]);

  const jumpTo = (idx) => {
    if (idx === active) setTick((t) => t + 1);
    else setActive(idx);
  };

  // Ken Burns, sharpness-aware (photo quality audit, client 2026-10-10).
  // Any running transform is composited and GPU-resampled, so a photo is
  // ~15-20% softer WHILE it moves - unavoidable physics. The old recipe made
  // it worse and permanent: a forced layer (will-change/translateZ) plus a
  // zoom IN that never finished, so every slide sat GPU-stretched at 1.05 for
  // its whole hold (-28% measured). Now the drift runs the other way: the
  // frame arrives at 1.05 and settles to 1.0 within the hold, then the
  // transform is cleared so the photo is painted pixel-exact from the file.
  useEffect(() => {
    if (reduce || paused || gated || film) return;
    const el = slideRefs.current[active];
    if (!el) return;
    gsap.fromTo(
      el,
      { scale: 1.05 },
      {
        scale: 1.0,
        duration: SLIDE_MS / 1000,
        ease: "sine.out",
        overwrite: "auto",
        onComplete: () => gsap.set(el, { clearProps: "transform" }),
      }
    );
  }, [active, reduce, paused, gated, film]);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    initReveals(el);

    let disposeFit;
    const trackEl = wordmarkTrackRef.current;
    const wmEl = wordmarkRef.current;
    if (trackEl && wmEl) {
      disposeFit = fitTextToWidth(wmEl, {
        container: trackEl,
        minFontPx: 32,
        maxFontPx: 260,
        paddingFactor: 1.0,
      });
    }

    // Reserve hero content padding so CTAs/headline never intersect wordmark.
    const reserveContentSpace = () => {
      if (!wmEl || !contentRef.current) return;
      const wmH = wmEl.getBoundingClientRect().height;
      if (wmH <= 0) return;
      const trackBottom = trackEl
        ? parseFloat(getComputedStyle(trackEl).bottom) || 0
        : 0;
      const gap = 24;
      contentRef.current.style.paddingBottom = `${Math.round(wmH + trackBottom + gap)}px`;
    };
    let ro;
    if ("ResizeObserver" in window && wmEl) {
      ro = new ResizeObserver(() => reserveContentSpace());
      ro.observe(wmEl);
    }
    const rafId = requestAnimationFrame(reserveContentSpace);
    if (document.fonts && typeof document.fonts.ready?.then === "function") {
      document.fonts.ready.then(reserveContentSpace).catch(() => {});
    }
    const spaceT1 = setTimeout(reserveContentSpace, 400);
    const onResizeSpace = () => reserveContentSpace();
    window.addEventListener("resize", onResizeSpace);

    // Single-element wordmark scrub (Rennsport): the nav's own "GRID MASTERS"
    // text is transformed from this hero placeholder's rect to its nav rect.
    let kill;
    const start = () => {
      try {
        if (!morph) { document.body.setAttribute("data-wm-state", "static"); return; }
        if (!navWordmarkRef?.current || !navMorphRef?.current || !wordmarkRef.current) {
          document.body.setAttribute("data-wm-state", "static");
          return;
        }
        kill = initHeroWordmark({
          hero: el,
          placeholder: wordmarkRef.current,
          sizer: navWordmarkRef.current,
          morph: navMorphRef.current,
        });
        ScrollTrigger.refresh();
      } catch (err) {
        // Fallback: two static elements (placeholder in hero + nav lockup).
        document.body.setAttribute("data-wm-state", "static");
        console.warn("[Hero] wordmark init failed:", err);
      }
    };
    let cancelled = false;
    const kickStart = () => {
      if (cancelled) return;
      requestAnimationFrame(() => { if (!cancelled) start(); });
    };
    // Start straight away (next frame, after the fit-to-width pass above has
    // its first measure). initHeroWordmark gates its own build on the two
    // display faces (whenDisplayFontReady) and re-syncs on every late metric
    // change. This used to wait for document.fonts.ready, which WebKit only
    // resolves once EVERY font on the page has settled - 1-3s after the
    // Loader (which waits for the display faces only) had revealed the site.
    // In that window body[data-wm-state] was still "boot": giant wordmark in
    // the hero, no nav lockup, no scrub (client, 2026-10-22: "the nav logo
    // starts working after a scroll or two").
    kickStart();
    const t2 = setTimeout(() => ScrollTrigger.refresh(), 900);

    // Hero content scroll-out: fade headline / progress / CTAs to 0 BEFORE
    // they would visually collide with the sticky nav band. Scrubbed, no
    // y translate — a translate up would just accelerate them into the nav
    // and create the "half text under the nav" state the client flagged.
    // Range: from top-of-hero through 32% of the hero. By 32% the content
    // is opacity 0 and never occupies the nav band on any subsequent scroll.
    let contentST = null;
    if (contentRef.current) {
      const contentTween = gsap.to(contentRef.current, {
        opacity: 0,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top top",
          end: "32% top",
          scrub: true,
          invalidateOnRefresh: true,
        },
      });
      contentST = contentTween.scrollTrigger;
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      clearTimeout(t2);
      clearTimeout(spaceT1);
      window.removeEventListener("resize", onResizeSpace);
      ro?.disconnect();
      disposeFit?.();
      kill?.();
      contentST?.kill();
    };
  }, [navLogoRef, navWordmarkRef, navMorphRef, morph]);

  return (
    <section
      className={`gm-hero ${morph ? "" : "gm-hero--no-wordmark"}`}
      ref={heroRef}
      data-testid={testid}
      data-brand={brand.text === BRANDS.ultimatelap.text ? "ultimatelap" : "gridmasters"}
      data-hero-active-index={active}
    >
      <div className="gm-hero__slides" aria-hidden="true">
        {SLIDES.map((s, i) => {
          // A slide is a URL or { src, pos }: `pos` is the photo's focal point
          // (where the car is) so `cover` keeps it clear of the headline and
          // wordmark on wide AND tall viewports (client, 2026-09-15).
          const src = typeof s === "string" ? s : s.src;
          const pos = typeof s === "string" ? undefined : s.pos;
          const video = typeof s === "string" ? undefined : s.video;
          if (video) {
            // Living slide: a silent loop in the photo's place. The poster is
            // the loop's first frame so the crossfade in is seamless; the
            // element mounts once its turn is near (`seen`) like the photos.
            return (
              <div
                key={video}
                ref={(el) => (slideRefs.current[i] = el)}
                className={`gm-hero__slide gm-hero__slide--video ${i === active ? "is-active" : ""}`}
                style={{ backgroundImage: `url(${s.poster})`, backgroundPosition: pos }}
                data-testid={`hero-slide-${i}`}
              >
                {seen.has(i) && (
                  <HeroVideo src={video} poster={s.poster} pos={pos} loop={!!s.loop} active={i === active} paused={paused || reduce} testid={`hero-video-${i}`} onPlaying={film ? onFilmPlaying : undefined} />
                )}
              </div>
            );
          }
          if (s.hd) {
            // HD photo slide: <picture> with AVIF + WebP srcsets; the browser
            // picks the file for its own width x DPR. Mounted once its turn is
            // near (`seen`) like the background-image slides; slide 0 is the
            // LCP candidate and is also preloaded from index.html.
            return (
              <div
                key={src}
                ref={(el) => (slideRefs.current[i] = el)}
                className={`gm-hero__slide gm-hero__slide--img ${i === active ? "is-active" : ""}`}
                data-testid={`hero-slide-${i}`}
              >
                {seen.has(i) && (
                  <picture>
                    {(s.sources || []).map((so) => (
                      <source key={so.type} type={so.type} srcSet={so.srcSet} sizes={s.srcSet ? "100vw" : undefined} />
                    ))}
                    <img
                      src={src}
                      srcSet={s.srcSet}
                      sizes={s.srcSet ? "100vw" : undefined}
                      alt=""
                      className="gm-hero__img"
                      style={{ objectPosition: pos }}
                      decoding="async"
                      // Current + next frame win the bandwidth race against the
                      // rest of the page's (eagerly loaded) photos; the others
                      // queue behind them (slow-line trace, 2026-10-22).
                      fetchPriority={i === active || i === (active + 1) % SLIDES.length ? "high" : "auto"}
                      draggable={false}
                    />
                  </picture>
                )}
              </div>
            );
          }
          return (
            <div
              key={src}
              ref={(el) => (slideRefs.current[i] = el)}
              className={`gm-hero__slide ${i === active ? "is-active" : ""}`}
              style={seen.has(i) ? { backgroundImage: `url(${src})`, backgroundPosition: pos } : undefined}
              data-testid={`hero-slide-${i}`}
            />
          );
        })}
        <div className="gm-hero__scrim"></div>
        <div className="gm-hero__vignette"></div>
      </div>

      <div className="gm-hero__content container-gm" ref={contentRef}>
        <div className="gm-hero__headline">
          {/* Client, 2026-10-08: the landing passes title="" - no line of copy
              above the lockup at all. An invisible h1 keeps the page outline /
              SEO intact. Other heroes (Ultimate Lap) keep their line. */}
          {title ? (
            <h1 className="gm-hero__title" data-reveal="mask">
              {title}
            </h1>
          ) : (
            <h1 className="gm-hero__title gm-hero__title--sr" data-testid="hero-title-sr">{brand?.text || "GRID MASTERS"}</h1>
          )}

          {SLIDES.length > 1 && <div
            className="gm-hero__progress"
            role="tablist"
            aria-label="Hero image selector"
            data-testid="hero-progress"
          >
            {SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === active}
                aria-label={`Show hero image ${i + 1} of ${SLIDES.length}`}
                className={`gm-hero__progress-line ${i === active ? "is-active" : ""} ${i < active ? "is-past" : ""}`}
                data-testid={`hero-progress-line-${i}`}
                onClick={() => jumpTo(i)}
              >
                <span
                  className="gm-hero__progress-fill"
                  style={{
                    animationDuration: reduce ? "0s" : `${SLIDE_MS}ms`,
                    animationName: i === active && !reduce && !paused && !gated ? "gmHeroFill" : "none",
                  }}
                  key={i === active ? `active-${active}-${tick}-${gated ? "held" : "run"}` : `idle-${i}`}
                />
              </button>
            ))}
          </div>}

          {ctas.length > 0 && <div className="gm-hero__cta">
            {ctas.map((c) => {
              // Phones (client, 2026-09-17): the hero CTA is the see-through
              // outlined button, not the yellow fill.
              const solid = !phone || keepFill;
              const fill = c.fill && solid;
              const white = c.white && solid;
              const cls = `btn btn--pill ${fill ? "btn--fill" : ""} ${white ? "btn--white" : ""} ${c.ghost ? "btn--ghost" : ""} ${c.wide ? "btn--wide" : ""} ${c.disabled ? "btn--disabled" : ""}`;
              if (c.disabled) {
                // e.g. "Round 2 · slots opening soon" - visible, not actionable.
                return <span key={c.testid || c.label} className={cls} aria-disabled="true" title={c.title} data-testid={c.testid}>{c.label}</span>;
              }
              // Internal paths use the router (no full reload); hashes stay anchors.
              return c.href.startsWith("/") && !c.download ? (
                <Link key={c.testid || c.label} className={cls} to={c.href} data-testid={c.testid}>{c.label}</Link>
              ) : (
                <a key={c.testid || c.label} className={cls} href={c.href} download={c.download || undefined} data-testid={c.testid}>{c.label}</a>
              );
            })}
          </div>}
        </div>
      </div>

      {morph && (
        <div className="gm-hero__wordmark-track" ref={wordmarkTrackRef} aria-hidden="true">
          {/* Placeholder lockup: identical structure to the nav lockup, fitted to
              the track width. Defines the giant start rect; hidden once live. */}
          <span className="gm-lockup gm-hero__wordmark" ref={wordmarkRef} data-testid="hero-wordmark">
            <img src={brand.mark} alt="" className="gm-lockup__mark" decoding="sync" />
            <span className="gm-lockup__text">{brand.text}</span>
          </span>
        </div>
      )}
    </section>
  );
}
